import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SchooleRegistration from "./SchoolRegistration";
import SchooleRegistrationService from "./Locality/Service/schooleRegistration";
import { saveParentSchoolSessionBeforeSwitch, isViewingChildSchool, getParentSchoolSession } from "../../utils/schoolImpersonation";
import SchoolIcon from "@material-ui/icons/School";
import PeopleIcon from "@material-ui/icons/People";
import PersonIcon from "@material-ui/icons/Person";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import AssignmentIcon from "@material-ui/icons/Assignment";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddIcon from "@material-ui/icons/Add";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.9rem",
    color: "#0f2d4d",
    lineHeight: 1.2,
  },
  pageSubtitle: {
    color: "#6b7280",
    fontSize: "0.9rem",
    marginTop: 2,
  },
  primaryBtn: {
    backgroundColor: "#1769d6",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    padding: "8px 18px",
    boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
    "&:hover": { backgroundColor: "#125bbd" },
  },
  noteBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    backgroundColor: "#eef4ff",
    border: "1px solid #d6e4ff",
    marginBottom: theme.spacing(2),
  },
  noteIconWrap: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#1769d6",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  summaryCard: {
    borderRadius: 14,
    padding: theme.spacing(2),
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "100%",
    boxSizing: "border-box",
  },
  summaryLabel: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#46566b",
    marginBottom: 4,
  },
  summaryValue: {
    fontWeight: 800,
    fontSize: "1.75rem",
    color: "#0f2d4d",
    lineHeight: 1.1,
  },
  summaryIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  contentCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    overflow: "hidden",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #eef1f5",
  },
  iconBtnOutlined: {
    border: "1px solid #e6eaf0",
    borderRadius: 8,
    padding: 7,
  },
  schoolCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    cursor: "pointer",
    height: "100%",
    transition: "box-shadow 0.2s ease, transform 0.15s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(16,42,67,0.1)",
      transform: "translateY(-2px)",
    },
  },
  schoolCardParent: {
    borderLeft: "4px solid #2e7d32",
  },
  schoolCardChild: {
    borderLeft: "4px solid #1769d6",
  },
  schoolName: {
    fontWeight: 700,
    color: "#0f2d4d",
    fontSize: "1rem",
  },
  statRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.8125rem",
    color: "#46566b",
  },
  statValue: {
    fontWeight: 700,
    color: "#0f2d4d",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6, 2),
    color: "#7a8aa0",
  },
  detailHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  backBtn: {
    textTransform: "none",
    fontWeight: 600,
    color: "#46566b",
    borderRadius: 8,
  },
  detailsCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    marginTop: theme.spacing(2),
  },
  detailField: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  detailLabel: {
    fontSize: "0.72rem",
    fontWeight: 600,
    color: "#9aa6b6",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  detailValue: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#0f2d4d",
  },
  parentChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    height: 24,
  },
  childChip: {
    backgroundColor: "#eef4ff",
    color: "#1769d6",
    fontWeight: 600,
    height: 24,
  },
}));

const SUMMARY_ITEMS = [
  { key: "schools", label: "Total Schools", icon: SchoolIcon, bg: "#eef4ff", color: "#1769d6" },
  { key: "parent", label: "Parent School", icon: SchoolIcon, bg: "#e8f5e9", color: "#2e7d32" },
  { key: "child", label: "Child Schools", icon: SchoolIcon, bg: "#eef4ff", color: "#1769d6" },
  { key: "students", label: "Total Students", icon: PeopleIcon, bg: "#f3efff", color: "#7c4dff" },
  { key: "teachers", label: "Total Teachers", icon: PersonIcon, bg: "#fff3e0", color: "#e65100" },
  { key: "attendance", label: "Attendance Today", icon: EventAvailableIcon, bg: "#e0f7fa", color: "#00838f" },
];

export default function AddBranchSchool(props) {
  const classes = useStyles();
  const [showForm, setShowForm] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [parentSchoolId, setParentSchoolId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [branchSchools, setBranchSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loggingInSchool, setLoggingInSchool] = useState(false);
  const [noteOpen, setNoteOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isBranchView, setIsBranchView] = useState(false);
  const [currentSchoolId, setCurrentSchoolId] = useState(null);

  const resolveId = (id) => (id && typeof id === "object" ? id._id : id);

  const isMasterSchool = (school) =>
    Boolean(parentSchoolId && String(resolveId(school._id)) === String(parentSchoolId));

  const fetchBranchSchools = () => {
    if (!userId) return;
    setLoading(true);
    SchooleRegistrationService.getSchoolsWithStatsForAdmin(userId)
      .then((schools) => {
        setBranchSchools(Array.isArray(schools) ? schools : []);
      })
      .catch(() => setBranchSchools([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const loadContext = async () => {
      const userDetails = localStorage.getItem("userDetail")
        ? JSON.parse(localStorage.getItem("userDetail"))
        : {};

      if (!userDetails || (userDetails.role !== "SCHOOLE" && userDetails.role !== "SUPERADMIN")) {
        return;
      }

      const schoolId = resolveId(userDetails.schoolId || userDetails.schooleId || userDetails._id);
      setCurrentSchoolId(schoolId);
      setUserId(userDetails._id);

      const session = getParentSchoolSession();
      let masterId = session?.parentSchoolId || null;
      let branchView = isViewingChildSchool();

      if (schoolId) {
        try {
          const school = await SchooleRegistrationService.getSchoolById(schoolId);
          if (school?.parentSchoolId) {
            branchView = true;
            masterId = masterId || resolveId(school.parentSchoolId);
          } else if (!masterId) {
            masterId = resolveId(school?._id) || schoolId;
          }
        } catch {
          if (!masterId) masterId = schoolId;
        }
      }

      setParentSchoolId(masterId);
      setIsBranchView(branchView);
    };

    loadContext();
  }, []);

  useEffect(() => {
    if (userId && !showForm) {
      fetchBranchSchools();
    }
  }, [userId, showForm]);

  const stats = useMemo(() => {
    const totalSchools = branchSchools.length;
    const parentCount = branchSchools.filter((s) => isMasterSchool(s)).length;
    const childCount = totalSchools - parentCount;
    const totalStudents = branchSchools.reduce((acc, s) => acc + (s.studentCount || 0), 0);
    const totalTeachers = branchSchools.reduce((acc, s) => acc + (s.teacherCount || 0), 0);
    const totalPresent = branchSchools.reduce((acc, s) => acc + (s.attendancePresentToday || 0), 0);
    const totalAbsent = branchSchools.reduce((acc, s) => acc + (s.attendanceAbsentToday || 0), 0);
    return {
      totalSchools,
      parentCount,
      childCount,
      totalStudents,
      totalTeachers,
      totalPresent,
      totalAbsent,
    };
  }, [branchSchools, parentSchoolId]);

  const summaryValues = {
    schools: stats.totalSchools,
    parent: stats.parentCount,
    child: stats.childCount,
    students: stats.totalStudents,
    teachers: stats.totalTeachers,
    attendance: null,
  };

  const filteredSchools = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return branchSchools;
    return branchSchools.filter((row) => {
      const name = (row.schooleName || "").toLowerCase();
      const city = (row.city || "").toLowerCase();
      const contact = (row.name || "").toLowerCase();
      return name.includes(term) || city.includes(term) || contact.includes(term);
    });
  }, [branchSchools, searchTerm]);

  const handleLoginAsSchool = async (school) => {
    try {
      setLoggingInSchool(true);
      saveParentSchoolSessionBeforeSwitch(school);
      const res = await SchooleRegistrationService.loginAsSchool(school._id);
      localStorage.setItem("userDetail", JSON.stringify(res));
      localStorage.setItem("id_token", res.token);
      props.history.push("/app/studentdetails");
      window.location.reload();
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Unable to login as this school");
    } finally {
      setLoggingInSchool(false);
    }
  };

  const renderStatPills = (row) => (
    <Box className={classes.statRow}>
      <span className={classes.statItem}>
        <PeopleIcon style={{ fontSize: 16, color: "#7c4dff" }} />
        <span className={classes.statValue}>{row.studentCount ?? 0}</span> Students
      </span>
      <span className={classes.statItem}>
        <PersonIcon style={{ fontSize: 16, color: "#e65100" }} />
        <span className={classes.statValue}>{row.teacherCount ?? 0}</span> Teachers
      </span>
      <span className={classes.statItem}>
        <span className={classes.statValue} style={{ color: "#2e7d32" }}>P: {row.attendancePresentToday ?? 0}</span>
        <span>/</span>
        <span className={classes.statValue} style={{ color: "#c62828" }}>A: {row.attendanceAbsentToday ?? 0}</span>
      </span>
      <span className={classes.statItem}>
        <AssignmentIcon style={{ fontSize: 16, color: "#00838f" }} />
        <span className={classes.statValue}>{row.activityCount ?? 0}</span> Activities
      </span>
    </Box>
  );

  const renderSummaryCards = (schoolOverride) => {
    const isSingle = !!schoolOverride;
    const values = isSingle
      ? {
          schools: 1,
          parent: isMasterSchool(schoolOverride) ? 1 : 0,
          child: isMasterSchool(schoolOverride) ? 0 : 1,
          students: schoolOverride.studentCount ?? 0,
          teachers: schoolOverride.teacherCount ?? 0,
          attendance: null,
        }
      : summaryValues;

    const present = isSingle ? (schoolOverride.attendancePresentToday ?? 0) : stats.totalPresent;
    const absent = isSingle ? (schoolOverride.attendanceAbsentToday ?? 0) : stats.totalAbsent;

    return (
      <Box className={classes.summaryGrid}>
        {SUMMARY_ITEMS.map(({ key, label, icon: Icon, bg, color }) => (
          <Box key={key} className={classes.summaryCard}>
            <Box>
              <Typography className={classes.summaryLabel}>{label}</Typography>
              {key === "attendance" ? (
                <Typography className={classes.summaryValue} style={{ fontSize: "1.25rem" }}>
                  <span style={{ color: "#2e7d32" }}>P: {present}</span>
                  <span style={{ margin: "0 6px", color: "#9aa6b6" }}>/</span>
                  <span style={{ color: "#c62828" }}>A: {absent}</span>
                </Typography>
              ) : (
                <Typography className={classes.summaryValue}>{values[key] ?? 0}</Typography>
              )}
            </Box>
            <Box className={classes.summaryIcon} style={{ backgroundColor: bg, color }}>
              <Icon style={{ fontSize: 24 }} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderSchoolDetails = (school) => {
    return (
      <Card elevation={0} className={classes.detailsCard}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.detailField}>
                <LocationOnIcon style={{ color: "#1769d6", fontSize: 20, marginTop: 2 }} />
                <Box>
                  <Typography className={classes.detailLabel}>City</Typography>
                  <Typography className={classes.detailValue}>{school.city || "—"}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.detailField}>
                <PersonIcon style={{ color: "#1769d6", fontSize: 20, marginTop: 2 }} />
                <Box>
                  <Typography className={classes.detailLabel}>Contact Person</Typography>
                  <Typography className={classes.detailValue}>{school.name || "—"}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.detailField}>
                <PhoneIcon style={{ color: "#1769d6", fontSize: 20, marginTop: 2 }} />
                <Box>
                  <Typography className={classes.detailLabel}>Contact Number</Typography>
                  <Typography className={classes.detailValue}>
                    {school.schooleContact || school.mobileNumber || "—"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.detailField}>
                <EmailIcon style={{ color: "#1769d6", fontSize: 20, marginTop: 2 }} />
                <Box>
                  <Typography className={classes.detailLabel}>School Email</Typography>
                  <Typography className={classes.detailValue}>
                    {school.schooleEmail || school.email || "—"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            {school.address && (
              <Grid item xs={12}>
                <Box className={classes.detailField}>
                  <LocationOnIcon style={{ color: "#1769d6", fontSize: 20, marginTop: 2 }} />
                  <Box>
                    <Typography className={classes.detailLabel}>Address</Typography>
                    <Typography className={classes.detailValue}>{school.address}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (!parentSchoolId) {
    return null;
  }

  if (selectedSchool) {
    const isParent = isMasterSchool(selectedSchool);
    const isActiveSchool = String(resolveId(selectedSchool._id)) === String(currentSchoolId);
    return (
      <Box className={classes.pageWrapper}>
        <Box className={classes.detailHeader}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setSelectedSchool(null)}
            className={classes.backBtn}
          >
            Back to schools
          </Button>
          <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={12}>
            <Typography className={classes.pageTitle} style={{ fontSize: "1.5rem" }}>
              {selectedSchool.schooleName || "—"}
            </Typography>
            <Chip
              size="small"
              label={isParent ? "Parent School" : "Child School"}
              className={isParent ? classes.parentChip : classes.childChip}
            />
          </Box>
          {!isActiveSchool && (
            <Button
              variant="contained"
              className={classes.primaryBtn}
              startIcon={<ExitToAppIcon />}
              onClick={() => handleLoginAsSchool(selectedSchool)}
              disabled={loggingInSchool}
            >
              {loggingInSchool ? "Logging in…" : "Login as school"}
            </Button>
          )}
        </Box>

        {renderSummaryCards(selectedSchool)}

        <Typography style={{ fontWeight: 700, color: "#0f2d4d", marginBottom: 8 }}>
          School Details
        </Typography>
        {renderSchoolDetails(selectedSchool)}
      </Box>
    );
  }

  if (showForm && !isBranchView) {
    return (
      <SchooleRegistration
        {...props}
        parentSchoolId={parentSchoolId}
        onBack={() => {
          setShowForm(false);
          fetchBranchSchools();
        }}
      />
    );
  }

  return (
    <Box className={classes.pageWrapper}>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography className={classes.pageTitle}>Branch Schools</Typography>
          <Typography className={classes.pageSubtitle}>
            {isBranchView
              ? "View branch school details and stats. Return to the parent school to add new branches."
              : "Manage parent and child schools, view stats, and switch into a branch dashboard."}
          </Typography>
        </Box>
        {!isBranchView && (
          <Button
            variant="contained"
            className={classes.primaryBtn}
            startIcon={<AddIcon />}
            onClick={() => setShowForm(true)}
          >
            Add Branch School
          </Button>
        )}
      </Box>

      {noteOpen && (
        <Box className={classes.noteBanner}>
          <Box className={classes.noteIconWrap}>
            <InfoOutlinedIcon style={{ fontSize: 18 }} />
          </Box>
          <Box flex={1}>
            <Typography style={{ fontWeight: 600, color: "#1f3a5f", fontSize: "0.9rem" }}>
              {isBranchView ? "Branch school view" : "Branch school management"}
            </Typography>
            <Typography style={{ color: "#46566b", fontSize: "0.85rem", marginTop: 2 }}>
              {isBranchView
                ? "You are viewing a branch school. Use Back to Parent School in the header to return to the parent account."
                : "Click a school card to view details and login as that school. Use Add Branch School to register a new child location under your parent account."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setNoteOpen(false)}>
            <CloseIcon style={{ fontSize: 18, color: "#7a8aa0" }} />
          </IconButton>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <CircularProgress style={{ color: "#1769d6" }} />
        </Box>
      ) : (
        <>
          {renderSummaryCards()}

          <Card elevation={0} className={classes.contentCard}>
            <Box className={classes.toolbar}>
              <Typography style={{ fontWeight: 700, color: "#0f2d4d" }}>
                {filteredSchools.length} School{filteredSchools.length !== 1 ? "s" : ""}
              </Typography>
              <Box display="flex" alignItems="center" gridGap={10}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Search schools…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ fontSize: 18, color: "#9aa6b6" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Tooltip title="Refresh">
                  <IconButton className={classes.iconBtnOutlined} size="small" onClick={fetchBranchSchools}>
                    <RefreshIcon style={{ fontSize: 20, color: "#46566b" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {filteredSchools.length === 0 ? (
              <Box className={classes.emptyState}>
                <SchoolIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.4 }} />
                <Typography>
                  {branchSchools.length === 0
                    ? (isBranchView
                        ? "No branch schools found."
                        : "No branch schools registered yet. Add one to get started.")
                    : "No schools match your search."}
                </Typography>
              </Box>
            ) : (
              <Box p={2}>
                <Grid container spacing={2}>
                  {filteredSchools.map((row) => {
                    const isParent = isMasterSchool(row);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={row._id}>
                        <Card
                          elevation={0}
                          className={`${classes.schoolCard} ${isParent ? classes.schoolCardParent : classes.schoolCardChild}`}
                          onClick={() => setSelectedSchool(row)}
                        >
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                              <Typography className={classes.schoolName}>
                                {row.schooleName || "—"}
                              </Typography>
                              <Chip
                                size="small"
                                label={isParent ? "Parent" : "Child"}
                                className={isParent ? classes.parentChip : classes.childChip}
                              />
                            </Box>
                            <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 8 }}>
                              {[row.city, row.name].filter(Boolean).join(" · ") || "—"}
                            </Typography>
                            {renderStatPills(row)}
                            {(row.schooleContact || row.mobileNumber) && (
                              <Typography variant="caption" display="block" style={{ marginTop: 10, color: "#9aa6b6" }}>
                                Contact: {row.schooleContact || row.mobileNumber}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Card>
        </>
      )}
    </Box>
  );
}
